import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Star, Phone } from "lucide-react";
import { IMAGES, PHONE_DISPLAY, PHONE_LINK } from "@/lib/data";
import { scrollToId } from "@/App";

const LINES = ["Your Home,", "Wrapped in Warm", "Winter Light."];

const BULBS = [
  { top: "12%", left: "6%", delay: "0s" },
  { top: "22%", left: "88%", delay: "0.7s" },
  { top: "68%", left: "4%", delay: "1.3s" },
  { top: "16%", left: "45%", delay: "1.9s" },
  { top: "74%", left: "92%", delay: "0.4s" },
  { top: "45%", left: "95%", delay: "1.6s" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={IMAGES.hero} alt="Southern home glowing with warm Christmas lights" className="h-full w-full object-cover" style={{ objectPosition: "center 72%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06130D]/70 via-[#06130D]/40 to-[#06130D]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06130D]/70 via-transparent to-transparent" />
      </motion.div>

      {BULBS.map((b, i) => (
        <span
          key={i}
          className="animate-twinkle animate-filament absolute z-10 h-2 w-2 rounded-full bg-amber-300"
          style={{ top: b.top, left: b.left, animationDelay: b.delay }}
        />
      ))}

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-amber-500/25 bg-[#0B1D14]/70 px-4 py-2 backdrop-blur-md"
          data-testid="hero-eyebrow"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-filament" />
          <span className="font-mono-x text-[11px] uppercase tracking-[0.28em] text-amber-300">
            Atlanta & North Georgia · Booking Nov–Dec 2026
          </span>
        </motion.div>

        <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-amber-50 sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-amber-400 text-glow-gold" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-emerald-100/80 sm:text-lg"
        >
          Professional Christmas light installation, maintenance and January takedown for Southern homes.
          Pick one of three packages, secure your spot with a 50% deposit — we handle every ladder, clip and bulb.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("packages")}
            data-testid="hero-explore-button"
            className="group rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-8 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 transition-all hover:from-amber-400 hover:to-amber-300 gold-glow"
          >
            Explore the 3 Packages
            <ArrowDown className="ml-2 inline transition-transform group-hover:translate-y-1" size={16} />
          </button>
          <a
            href={PHONE_LINK}
            data-testid="hero-call-button"
            className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-950/60 px-8 py-4 text-sm font-bold uppercase tracking-widest text-emerald-100 backdrop-blur-md transition-all hover:border-amber-400/50 hover:bg-emerald-900/60"
          >
            <Phone size={15} className="text-amber-400" />
            {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 1 }}
          className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono-x text-[11px] uppercase tracking-[0.22em] text-emerald-100/60"
          data-testid="hero-trust-ticker"
        >
          <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-amber-400" /> Licensed & Insured</span>
          <span className="flex items-center gap-2"><Star size={14} className="text-amber-400" /> 5-Star Rated Crews</span>
          <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-filament" /> 48-Hr Storm Guarantee</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
